import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FaBars, FaTimes, FaChevronDown } from 'react-icons/fa';

const Nav = styled.nav`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  background: linear-gradient(135deg, rgba(0, 20, 40, 0.95) 0%, rgba(0, 30, 60, 0.95) 50%, rgba(0, 20, 40, 0.95) 100%);
  backdrop-filter: blur(10px) saturate(180%);
  -webkit-backdrop-filter: blur(10px) saturate(180%);
  box-shadow: 0 8px 32px 0 rgba(0, 123, 255, 0.15),
              0 2px 8px 0 rgba(0, 0, 0, 0.3);
  border-bottom: 1px solid rgba(0, 123, 255, 0.2);
  transition: all 0.3s ease;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, 
      transparent 0%, 
      rgba(0, 123, 255, 0.5) 50%, 
      transparent 100%);
  }
`;

const NavContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 92px;
`;

const Brand = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
`;

const BrandImg = styled.img`
  height: 60px;
  width: 60px;
  border-radius: 50%;
  object-fit: cover;
  transition: all 0.3s ease;
  filter: drop-shadow(0 2px 8px rgba(0, 123, 255, 0.3));

  &:hover {
    transform: scale(1.05);
    filter: drop-shadow(0 4px 12px rgba(0, 123, 255, 0.5));
  }
  
  @media (max-width: 768px) {
    height: 50px;
    width: 50px;
  }
`;

const BrandText = styled.div`
  margin-left: 15px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  h1 {
    font-size: 1rem;
    font-weight: 600;
    color: #fff;
    margin: 0;
    line-height: 1.1;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  }
  
  p {
    font-size: 0.7rem;
    color: #bdc3c7;
    margin: 0;
    font-weight: 400;
  }
  
  @media (max-width: 768px) {
    margin-left: 12px;
    
    h1 {
      font-size: 0.9rem;
    }
    
    p {
      font-size: 0.6rem;
    }
  }
`;

const NavMenu = styled.ul`
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: 10px;

  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    left: ${props => props.isOpen ? '0' : '-100%'};
    width: 100%;
    height: 100vh;
    background: linear-gradient(135deg, rgba(0, 0, 0, 0.98) 0%, rgba(20, 20, 30, 0.98) 100%);
    backdrop-filter: blur(20px);
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    padding-top: 100px;
    transition: left 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: inset 0 0 100px rgba(0, 123, 255, 0.1);
  }
`;

const NavItem = styled.li`
  margin: 0 20px;

  @media (max-width: 768px) {
    margin: 20px 0;
  }
`;

const NavLink = styled(Link)`
  color: #fff;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
  position: relative;
  padding: 8px 0;
  font-family: 'Poppins', sans-serif;
  letter-spacing: 0.5px;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background: linear-gradient(90deg, #007bff, #00d4ff);
    transition: width 0.3s ease;
  }

  &:hover {
    color: #00d4ff;
    text-shadow: 0 0 10px rgba(0, 212, 255, 0.5);
  }

  &:hover::after {
    width: 100%;
  }
`;

const MobileMenuBtn = styled.button`
  display: none;
  background: linear-gradient(135deg, rgba(0, 123, 255, 0.2), rgba(0, 212, 255, 0.2));
  border: 1px solid rgba(0, 123, 255, 0.3);
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 24px;
  cursor: pointer;
  color: #fff;
  transition: all 0.3s ease;
  backdrop-filter: blur(5px);

  &:hover {
    background: linear-gradient(135deg, rgba(0, 123, 255, 0.3), rgba(0, 212, 255, 0.3));
    border-color: rgba(0, 212, 255, 0.5);
    box-shadow: 0 0 15px rgba(0, 123, 255, 0.4);
  }

  @media (max-width: 768px) {
    display: block;
  }
`;

const SolutionsDropdown = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  height: 100%;
`;

const SolutionsButton = styled(Link)`
  background: none;
  border: none;
  color: white;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 0;
  transition: all 0.3s ease;
  text-decoration: none;
  position: relative;
  height: 100%;
  
  &:hover {
    color: #007bff;
    text-shadow: 0 0 8px rgba(0, 123, 255, 0.6);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 2px;
    background: linear-gradient(90deg, #007bff, #00d4ff);
    transition: width 0.3s ease;
  }
  
  &:hover::after {
    width: 100%;
  }
`;

const DropdownContent = styled.div`
  position: absolute;
  top: calc(100% + 5px);
  left: 0;
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.95) 0%, rgba(20, 20, 30, 0.95) 50%, rgba(0, 0, 0, 0.95) 100%);
  backdrop-filter: blur(10px) saturate(180%);
  -webkit-backdrop-filter: blur(10px) saturate(180%);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 123, 255, 0.15),
              0 2px 8px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(0, 123, 255, 0.2);
  min-width: 250px;
  z-index: 1001;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-10px);
  transition: all 0.3s ease;
  
  ${SolutionsDropdown}:hover & {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }
`;

const DropdownItem = styled(Link)`
  display: block;
  color: white;
  text-decoration: none;
  padding: 12px 20px;
  transition: all 0.3s ease;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  
  &:hover {
    color: #007bff;
    text-shadow: 0 0 8px rgba(0, 123, 255, 0.6);
    background: rgba(0, 123, 255, 0.05);
    padding-left: 25px;
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  &:first-child {
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
  }
  
  &:last-child {
    border-bottom-left-radius: 12px;
    border-bottom-right-radius: 12px;
  }
`;

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  return (
    <Nav>
      <NavContainer>
        <Brand to="/" aria-label="DirectRoute NETWORKS">
          <BrandImg src="/logo.png" alt="DirectRoute NETWORKS" />
          <BrandText>
            <h1>Direct Route<br/>Networks</h1>
          </BrandText>
        </Brand>
          <NavMenu isOpen={isOpen}>
            <NavItem>
              <NavLink to="/" onClick={() => setIsOpen(false)}>Home</NavLink>
            </NavItem>
            <NavItem>
              <SolutionsDropdown>
                <SolutionsButton to="/solutions" onClick={() => setIsOpen(false)}>
                  Solutions <FaChevronDown style={{ fontSize: '0.8rem' }} />
                </SolutionsButton>
                <DropdownContent>
                  <DropdownItem to="/toll-free-origination" onClick={() => setIsOpen(false)}>
                    Toll-Free Origination
                  </DropdownItem>
                  <DropdownItem to="/inbound-origination" onClick={() => setIsOpen(false)}>
                    Inbound Origination
                  </DropdownItem>
                  <DropdownItem to="/did-services" onClick={() => setIsOpen(false)}>
                    DID Services
                  </DropdownItem>
                  <DropdownItem to="/toll-free-termination" onClick={() => setIsOpen(false)}>
                    Toll-Free Termination
                  </DropdownItem>
                  <DropdownItem to="/wholesale-termination" onClick={() => setIsOpen(false)}>
                    Wholesale Termination
                  </DropdownItem>
                  <DropdownItem to="/dialer-termination" onClick={() => setIsOpen(false)}>
                    Dialer Termination
                  </DropdownItem>
                </DropdownContent>
              </SolutionsDropdown>
            </NavItem>
            <NavItem>
              <NavLink to="/pricing" onClick={() => setIsOpen(false)}>Pricing</NavLink>
            </NavItem>
            <NavItem>
              <NavLink to="/about" onClick={() => setIsOpen(false)}>About</NavLink>
            </NavItem>
            <NavItem>
              <NavLink to="/blogs" onClick={() => setIsOpen(false)}>Blogs</NavLink>
            </NavItem>
            <NavItem>
              <NavLink to="/contact" onClick={() => setIsOpen(false)}>Contact</NavLink>
            </NavItem>
          </NavMenu>
        <MobileMenuBtn onClick={toggleMenu}>
          {isOpen ? <FaTimes /> : <FaBars />}
        </MobileMenuBtn>
      </NavContainer>
    </Nav>
  );
};

export default Navbar;



